import { Component, Input, OnInit } from '@angular/core';
import { BasicCartesianChartConfig } from '@app/data/interface/widget-config';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption, SeriesOption } from 'echarts';
import { Analyte, EnvironmentalDataPoint, Granularity } from '@app/data/services/types/EnvironmentalData';
import { EnvironmentalDataSource } from '@app/data/services/types/EnvironmentalDataSource';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';

export interface PolarRange {
  name: string;
  min: number;
  max: number;
  color: string;
}

/**
 * Represents the response from the environmental data API.
 */
type EnvironmentalDataResponse = {
  analytes: Analyte[];
  data: EnvironmentalDataPoint[];
  environmentalDataSources: EnvironmentalDataSource[];
  granularity: Granularity;
};

/**
 * I've really tried to use types in this file but the inheritence is making it difficult.
 */

@Component({
  selector: 'app-basic-polar-bar-chart',
  templateUrl: './basic-polar-bar-chart.component.html',
  styleUrls: ['./basic-polar-bar-chart.component.scss'],
})
export class BasicPolarBarChartComponent extends BaseFilteredEnvironmentalDataChartComponent implements OnInit {
  @Input() config!: BasicCartesianChartConfig;
  @Input() ranges!: PolarRange[];
  chartOptions: EChartsOption | null = null;
  id: string = 'basic-polar-bar-chart-x';

  ngOnInit() {
    super.ngOnInit();
    this.id = `basic-polar-bar-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';
  }
  /**
   * Formats the tooltip content for the chart.
   *
   * @param params The parameters containing the data for the tooltip.
   * @returns The formatted tooltip content.
   */
  tooltipFormatter = (params: any) => {
    let result = `<strong>${params[0].name}</strong><br/>`;
    params.forEach((item: any) => {
      if (item.value > 0) {
        result += `<strong>${item.marker} ${item.seriesName}:</strong> ${item.value.toFixed(2)} %<br/>`;
      }
    });
    return result;
  };

  /**
   * Maps a degree value to one of the segment indexes.
   *
   * Adjusts the complexity of the polar bar chart based on how the data is binned into segments.
   *
   * @param degree The amount of degrees each segment represents.
   * @returns The corresponding segment index
   */
  getAngleAxisIndex(degree: number): number {
    // Use the angleAxis data count from the ECharts config
    let angleAxisData: string[] | undefined;
    if (Array.isArray(this.chartOptions?.angleAxis)) {
      angleAxisData = (this.chartOptions?.angleAxis[0] as any)?.data;
    } else if (this.chartOptions?.angleAxis && (this.chartOptions.angleAxis as any).data) {
      angleAxisData = (this.chartOptions.angleAxis as any).data;
    }
    const segmentCount = Array.isArray(angleAxisData) ? angleAxisData.length : 16;
    const segmentSize = 360 / segmentCount;
    const offset = segmentSize / 2;
    const index = Math.floor(((degree - offset + 360) % 360) / segmentSize);
    return index;
  }

  /**
   * Populate the chart with data from the query responses.
   *
   * Handles aggregation of the data based on the date range to avoid analytes becoming cluttered.
   *
   * Supports aggregation of analyte data for different time intervals (e.g., month, week) based on
   * the selected date range.
   *
   * @param queryResponses The responses from the queries.
   */
  populateResponses = (queryResponses: EnvironmentalDataResponse[]) => {
    const dim0Data = queryResponses[0]?.data || [];
    const dim1Data = queryResponses[1]?.data || [];

    if (dim0Data.length === 0 || dim1Data.length === 0) {
      this.hasData = false;
      this.chartOptions = { title: { text: 'No data for selected date range.' } };
      return;
    }

    this.hasData = true;

    const pairedData = dim0Data
      .map((dim0Point) => {
        const dim1Point = dim1Data.find((dirPoint) => dirPoint.date_time === dim0Point.date_time);
        return {
          dim1Point: dim0Point.avg,
          dim2Point: dim1Point?.avg,
        };
      })
      .filter((p) => p.dim1Point != null && p.dim2Point != null);

    const rangeAggregation: { [rangeName: string]: number[] } = {};
    this.ranges.forEach((range) => {
      rangeAggregation[range.name] = new Array(16).fill(0);
    });

    const totalReadings = pairedData.length;
    pairedData.forEach((point) => {
      const directionIndex = this.getAngleAxisIndex(point.dim2Point!);
      const dim0Range = this.ranges.find((r) => point.dim1Point! >= r.min && point.dim1Point! < r.max);

      if (dim0Range) {
        rangeAggregation[dim0Range.name][directionIndex]++;
      }
    });

    const newSeries: SeriesOption[] = this.ranges.map((range) => {
      const counts = rangeAggregation[range.name];
      const percentages = counts.map((count) => (count / totalReadings) * 100);

      return {
        name: range.name,
        type: 'bar',
        coordinateSystem: 'polar',
        color: range.color,
        stack: 'polar-data',
        emphasis: {
          focus: 'series',
        },
        data: percentages,
      };
    });

    this.updateConfig(newSeries);
  };

  /**
   * Updates the chart configuration with additional overrides specific to this widget
   */
  private updateConfig(newSeries: SeriesOption[]) {
    this.chartOptions = {
      ...this.config.echartsConfig,
      series: newSeries,
      tooltip: {
        ...this.config.echartsConfig?.tooltip,
        formatter: this.tooltipFormatter,
      },
      legend: {
        ...this.config.echartsConfig?.legend,
        data: this.ranges.map((r) => r.name),
      },
    };
  }

  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  protected setWidgetData() {
    // If we don't have any data sources to look at, exit early.
    const preserveConfiguredLabels = Boolean(this.config?.dataConfig?.preserveConfiguredDataSourceLabels);
    const applicableDataSources = this.selectDataSources(this.config.dataConfig.dataSources, {
      preserveConfiguredLabels,
    });

    // If there's nothing to display....
    if (applicableDataSources.length === 0) {
      this.hasData = false;
      this.chartOptions = null;
      return;
    }

    // Set loading state - set hasData to true first so the widget wrapper becomes visible
    this.hasData = true;
    this.chartOptions = {
      title: {
        text: 'Loading...',
      },
    };

    // Get the data
    forkJoin(
      applicableDataSources.map((source: any) =>
        this.chartService
          .getEnvironmentalDataForSourceAndAnalyteByDate(
            source.environmentalDataSourceIdentifier,
            source.analyteIdentifier,
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'),
            this.startDate === this.endDate ? 'hour' : 'day'
          )
          .pipe(take(1))
      )
    ).subscribe({
      next: (queryResponses: EnvironmentalDataResponse[]) => this.populateResponses(queryResponses),
      error: (err: any) => {
        console.error(err);
        this.hasData = false;
        this.chartOptions = {
          title: {
            text: 'No data is available for the selected date range.',
          },
        };
      },
    });
  }
}
